<template>
  <main class="flex-1 bg-gray-50 dark:bg-white/3">
    <div class="py-6">
      <!-- Header Section -->
      <div class="max-w-(--breakpoint-2xl) mx-auto">
        <div class="md:flex md:items-center md:justify-between">
          <div class="flex-1 min-w-0">
            <PageBreadcrumb page-title="Pridať nový článok" />
            <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Vytvorte nový článok pre váš blog alebo web
            </p>
          </div>
          <div class="mt-4 flex md:mt-0 md:ml-4">
            <button @click="goBack" type="button"
              class="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-xs text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-500 dark:border-gray-700 dark:text-gray-300 dark:bg-gray-800 dark:hover:bg-white/5">
              <svg class="-ml-1 mr-2 size-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"
                stroke-width="1.5" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" />
              </svg>
              Späť
            </button>
          </div>
        </div>
      </div>

      <!-- Main Content -->
      <div class="max-w-(--breakpoint-2xl) mx-auto mt-8">
        <!-- Success State -->
        <div v-if="submitted" class="bg-white rounded-lg shadow-xs dark:bg-white/3">
          <div class="text-center py-12">
            <div class="mx-auto flex items-center justify-center size-12 rounded-full bg-green-100">
              <svg class="size-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
              </svg>
            </div>
            <h3 class="mt-4 text-lg font-medium text-gray-900 dark:text-white/90">Článok úspešne pridaný!</h3>
            <p class="mt-2 text-sm text-gray-500 dark:text-gray-400">Váš článok bol úspešne vytvorený a je teraz dostupný.</p>
            <div class="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
              <button @click="newPost"
                class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-xs text-white bg-brand-600 hover:bg-brand-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-500">
                Pridať ďalší článok
              </button>
              <button @click="backToList"
                class="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-500 dark:border-gray-700 dark:text-gray-300 dark:bg-gray-800 dark:hover:bg-white/5">
                Späť na zoznam článkov
              </button>
            </div>
          </div>
        </div>

        <!-- Form State -->
        <div v-else class="lg:grid lg:grid-cols-12 lg:gap-8">
          <!-- Categories Sidebar -->
          <div class="lg:col-span-3 space-y-6">
            <!-- Categories -->
            <div class="bg-white rounded-lg shadow-xs p-6 dark:bg-white/3">
              <h3 class="text-lg font-medium text-gray-900 mb-4 dark:text-white/90">Dostupné kategórie</h3>
              <div class="space-y-3">
                <div v-for="category in postCategories" :key="category.id"
                  class="p-3 border rounded-lg hover:bg-gray-50 cursor-pointer transition-colors dark:hover:bg-white/5"
                  :class="{ 'border-brand-500 bg-brand-50': post.category_id === category.id }"
                  @click="selectCategory(category.id)">
                  <div class="flex justify-between items-start">
                    <div>
                      <p class="text-sm font-medium text-gray-900 dark:text-white/90">{{ category.name }}</p>
                      <p class="text-xs text-gray-500 mt-1 dark:text-gray-400">{{ category.description }}</p>
                    </div>
                    <span
                      class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-white/90">
                      ID: {{ category.id }}
                    </span>
                  </div>
                </div>
                <div v-if="postCategories.length === 0" class="text-center py-4">
                  <p class="text-sm text-gray-500 dark:text-gray-400">Načítavam kategórie...</p>
                </div>
              </div>
            </div>

            <!-- Preview Section -->
            <div class="bg-white rounded-lg shadow-xs p-6 dark:bg-white/3">
              <h3 class="text-lg font-medium text-gray-900 mb-4 dark:text-white/90">Náhľad článku</h3>
              <div class="space-y-3">
                <div v-if="post.title" class="border-l-4 border-brand-500 pl-3">
                  <h4 class="text-sm font-semibold text-gray-900 line-clamp-2 dark:text-white/90">{{ post.title }}</h4>
                </div>
                <div v-else class="text-xs text-gray-400 italic">
                  Zadajte nadpis článku...
                </div>

                <div v-if="imagePreview" class="mt-2">
                  <img :src="imagePreview" alt="Náhľad" class="w-full h-20 object-cover rounded-sm">
                </div>

                <div v-if="post.content && post.content.length > 10" class="text-xs text-gray-600 dark:text-gray-300">
                  <div v-html="post.content.substring(0, 100) + '...'" class="prose prose-xs max-w-none"></div>
                </div>
                <div v-else class="text-xs text-gray-400 italic">
                  Pridajte obsah článku...
                </div>

                <div class="pt-2 border-t">
                  <div v-if="post.category_id"
                    class="inline-flex items-center px-2 py-1 rounded-full text-xs bg-brand-100 text-brand-800">
                    {{ getSelectedCategoryName() }}
                  </div>
                  <div v-else class="text-xs text-gray-400 italic">
                    Vyberte kategóriu
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Main Form -->
          <div class="mt-8 lg:mt-0 lg:col-span-9">
            <form @submit.prevent="savePost" class="space-y-6">
              <div class="bg-white shadow-xs rounded-lg dark:bg-white/3">
                <div class="px-6 py-6">
                  <h3 class="text-lg leading-6 font-medium text-gray-900 mb-6 dark:text-white/90">Informácie o článku</h3>

                  <!-- Title Field -->
                  <div class="space-y-1">
                    <label for="title" class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Nadpis článku <span class="text-red-500">*</span>
                    </label>
                    <FormKit type="text" id="title" name="title" v-model="post.title" :classes="{
                      input: 'mt-1 block w-full border-gray-300 rounded-md shadow-xs focus:ring-brand-500 focus:border-brand-500 sm:text-sm'
                    }" placeholder="Zadajte nadpis článku" validation="required|length:3,200" :validation-messages="{
                      required: 'Nadpis je povinný',
                      length: 'Nadpis musí mať 3-200 znakov'
                    }" />
                    <p class="text-xs text-gray-500 dark:text-gray-400">
                      Krátky a výstižny nadpis pomôže čitateľom nájsť váš článok
                      <span class="float-right"
                        :class="{ 'text-red-500': post.title.length > 200, 'text-amber-500': post.title.length > 180 }">
                        {{ post.title.length }}/200
                      </span>
                    </p>
                  </div>

                  <!-- Slug Field -->
                  <div class="space-y-1 mt-6">
                    <label for="slug" class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      <span class="flex items-center justify-between">
                        <span class="flex items-center">
                          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5"
                            stroke="currentColor" class="size-4 mr-2">
                            <path stroke-linecap="round" stroke-linejoin="round"
                              d="M13.19 8.688a4.5 4.5 0 011.242 7.244l-4.5 4.5a4.5 4.5 0 01-6.364-6.364l1.757-1.757m13.35-.622l1.757-1.757a4.5 4.5 0 00-6.364-6.364l-4.5 4.5a4.5 4.5 0 001.242 7.244" />
                          </svg>
                          URL Slug
                        </span>
                        <span class="text-xs text-gray-500 font-normal dark:text-gray-400">
                          (automaticky vygenerované)
                        </span>
                      </span>
                    </label>
                    <FormKit type="text" id="slug" name="slug" v-model="post.slug" :classes="{
                      input: 'mt-1 block w-full border-gray-300 rounded-md shadow-xs focus:ring-brand-500 focus:border-brand-500 sm:text-sm font-mono'
                    }" placeholder="url-slug-clanku" />
                    <p class="text-xs text-gray-500 dark:text-gray-400">
                      URL: https://bizinix.sk/post/{{ post.slug || 'url-slug' }}
                    </p>
                  </div>

                  <!-- Category Selection -->
                  <div class="space-y-1 mt-6">
                    <label for="category" class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Kategória <span class="text-red-500">*</span>
                    </label>
                    <FormKit type="select" id="category" v-model="post.category_id" name="category_id"
                      :classes="{ input: 'mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-brand-500 focus:border-brand-500 sm:text-sm rounded-md dark:border-gray-700' }"
                      validation="required">
                      <option value="" disabled>Vyberte kategóriu</option>
                      <option v-for="category in postCategories" :key="category.id" :value="category.id">
                        {{ category.name }} (ID: {{ category.id }})
                      </option>
                    </FormKit>
                    <p class="text-xs text-gray-500 dark:text-gray-400">Kategória pomáha organizovať články pre lepšiu navigáciu</p>
                  </div>

                  <!-- Content Editor -->
                  <div class="space-y-1 mt-6">
                    <RichTextEditor v-model="post.content" label="Obsah článku" id="content" :height="400" />
                    <p class="text-xs text-gray-500 dark:text-gray-400">Používajte editor na formátovanie textu, pridávanie odkazov a
                      ďalších prvkov.</p>
                  </div>

                  <!-- Image Upload -->
                  <div class="space-y-1 mt-6">
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Obrázok článku
                    </label>
                    <div
                      class="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md hover:border-gray-400 transition-colors dark:border-gray-700 dark:hover:border-gray-600"
                      @dragover.prevent @drop.prevent="handleDrop">
                      <div class="space-y-1 text-center">
                        <div v-if="imagePreview" class="mb-4">
                          <img :src="imagePreview" alt="Náhľad obrázka"
                            class="mx-auto h-32 w-auto rounded-lg shadow-md">
                          <div class="mt-2 flex justify-center space-x-2">
                            <button @click="removeImage" type="button"
                              class="px-3 py-1 text-xs bg-red-100 text-red-700 rounded-sm hover:bg-red-200 transition-colors">
                              Odstrániť
                            </button>
                            <label for="image-upload2"
                              class="px-3 py-1 text-xs bg-blue-100 text-blue-700 rounded-sm hover:bg-blue-200 transition-colors cursor-pointer">
                              Zmeniť
                              <FormKit type="file" id="image-upload2" name="image-upload" accept="image/*"
                                :classes="{ input: 'sr-only' }" @change="updateImage" />
                            </label>
                          </div>
                        </div>
                        <svg v-else class="mx-auto size-12 text-gray-400" stroke="currentColor" fill="none"
                          viewBox="0 0 48 48">
                          <path
                            d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                            stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                        </svg>
                        <div v-if="!imagePreview" class="flex text-sm text-gray-600 dark:text-gray-300">
                          <label for="image-upload"
                            class="relative cursor-pointer bg-white rounded-md font-medium text-brand-600 hover:text-brand-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-brand-500 dark:bg-white/3">
                            <span>Nahrať obrázok</span>
                            <FormKit type="file" id="image-upload" name="image-upload" accept="image/*"
                              :classes="{ input: 'sr-only' }" @change="updateImage" />
                          </label>
                          <p class="pl-1">alebo potiahnite sem</p>
                        </div>
                        <p class="text-xs text-gray-500 dark:text-gray-400">PNG, JPG, GIF do 10MB</p>
                        <p v-if="post.image" class="text-xs text-gray-600 mt-2 dark:text-gray-300">
                          Vybraný súbor: {{ post.image }}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Form Actions -->
                <div class="px-6 py-4 bg-gray-50 border-t border-gray-200 rounded-b-lg dark:bg-white/3 dark:border-gray-800">
                  <div class="flex justify-between">
                    <div class="text-xs text-gray-500 self-center dark:text-gray-400">
                      <kbd
                        class="px-2 py-1 text-xs font-semibold text-gray-800 bg-gray-100 border border-gray-200 rounded-sm dark:text-white/90 dark:bg-gray-800 dark:border-gray-800">Ctrl</kbd>
                      +
                      <kbd
                        class="px-2 py-1 text-xs font-semibold text-gray-800 bg-gray-100 border border-gray-200 rounded-sm dark:text-white/90 dark:bg-gray-800 dark:border-gray-800">S</kbd>
                      na rýchle uloženie
                    </div>
                    <div class="flex space-x-3">
                      <button type="button" @click="goBack"
                        class="inline-flex items-center px-4 py-2 border border-gray-300 shadow-xs text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-500 dark:border-gray-700 dark:text-gray-300 dark:bg-gray-800 dark:hover:bg-white/5">
                        Zrušiť
                      </button>
                      <button type="submit" :disabled="isSubmitting"
                        class="inline-flex items-center px-4 py-2 border border-transparent shadow-xs text-sm font-medium rounded-md text-white bg-brand-600 hover:bg-brand-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-500 disabled:opacity-50 disabled:cursor-not-allowed">
                        <svg v-if="isSubmitting" class="animate-spin -ml-1 mr-2 size-4 text-white" fill="none"
                          viewBox="0 0 24 24">
                          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4">
                          </circle>
                          <path class="opacity-75" fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z">
                          </path>
                        </svg>
                        {{ isSubmitting ? 'Ukladám...' : 'Publikovať článok' }}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  </main>

  <!-- Leave warning dialog -->
  <div v-show="isLeaveWarningOpen"
    class="fixed inset-0 bg-gray-600/75 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4 dark:bg-gray-900">
    <div class="relative bg-white rounded-xl shadow-2xl max-w-md w-full mx-auto transform transition-all dark:bg-white/3">
      <div class="bg-amber-50 px-6 pt-6 pb-4 rounded-t-xl">
        <div class="flex items-center">
          <div class="mx-auto shrink-0 flex items-center justify-center size-12 rounded-full bg-amber-100">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2"
              stroke="currentColor" class="size-6 text-amber-600">
              <path stroke-linecap="round" stroke-linejoin="round"
                d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
            </svg>
          </div>
        </div>
      </div>
      <div class="px-6 py-4">
        <h3 class="text-lg font-semibold text-gray-900 text-center mb-2 dark:text-white/90">Neuložené zmeny</h3>
        <p class="text-sm text-gray-600 text-center mb-6 dark:text-gray-300">
          Máte neuložené zmeny. Ak opustíte túto stránku, všetky zmeny budú stratené.
        </p>
        <div class="flex flex-col sm:flex-row gap-3"> <button type="button" @click="cancelLeave"
            class="flex-1 inline-flex justify-center items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors duration-200 dark:border-gray-700 dark:text-gray-300 dark:bg-gray-800 dark:hover:bg-white/5">
            Zostať na stránke
          </button>
          <button type="button" @click="confirmLeave"
            class="flex-1 inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-amber-600 hover:bg-amber-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500 transition-colors duration-200">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2"
              stroke="currentColor" class="size-4 mr-2">
              <path stroke-linecap="round" stroke-linejoin="round"
                d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15M12 9l-3 3m0 0l3 3m-3-3h12.75" />
            </svg>
            Opustiť stránku
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import PostDataService from '@/services/PostDataService'
import PostCategoryDataService, { type PostCategory } from '@/services/PostCategoryDataService'
import RichTextEditor from '@/components/RichTextEditor.vue'
import PageBreadcrumb from '@/components/common/PageBreadcrumb.vue'
import type Post from '@/types/Post'
import type ResponseData from '@/types/ResponseData'
import { toast } from 'vue3-toastify'
import 'vue3-toastify/dist/index.css'

export default defineComponent({
  name: 'post-add',
  components: {
    RichTextEditor,
    PageBreadcrumb,
  }, data() {
    return {
      post: {
        title: '', slug: '', content: '',
        image: '',
        category_id: undefined,
      } as Post,
      postCategories: [] as PostCategory[],
      submitted: false,
      isSubmitting: false,
      imageData: null as File | null,
      imagePreview: null as string | null,
      isLeaveWarningOpen: false, // For leave warning dialog
      confirmedLeave: false, // Track if user confirmed to leave
      pendingRoute: null as import('vue-router').RouteLocationNormalized | null, // Store the route to navigate to after confirmation
    }
  },
  async mounted() {
    await this.getPostCategories()

    // Add keyboard shortcuts
    document.addEventListener('keydown', this.handleKeyboardShortcuts)

    // Add beforeunload event for browser tab/window close
    window.addEventListener('beforeunload', this.handleBeforeUnload)
  },

  watch: {
    'post.title'(newTitle: string) {
      // Auto-generate slug when title changes
      if (newTitle && !this.post.id) {
        this.post.slug = this.generateSlug(newTitle)
      }
    }
  },

  beforeUnmount() {
    document.removeEventListener('keydown', this.handleKeyboardShortcuts)
    window.removeEventListener('beforeunload', this.handleBeforeUnload)
  },

  methods: {
    handleKeyboardShortcuts(event: KeyboardEvent) {
      // Ctrl+S to save (prevent default browser save)
      if ((event.ctrlKey || event.metaKey) && event.key === 's') {
        event.preventDefault()
        if (!this.isSubmitting) {
          this.savePost()
        }
      }

      // Ctrl+Enter to save and submit
      if ((event.ctrlKey || event.metaKey) && event.key === 'Enter') {
        event.preventDefault()
        if (!this.isSubmitting) {
          this.savePost()
        }
      }
    }, async getPostCategories() {
      try {
        const response = await PostCategoryDataService.getAll()
        this.postCategories = (response.data as any)?.data || response.data

        // Set default category if available
        if (this.postCategories.length > 0 && !this.post.category_id) {
          this.post.category_id = this.postCategories[0].id
        }
      } catch (error: unknown) {
        console.error('Error loading categories:', error)
        const errorMessage = error instanceof Error ? error.message : 'Neznáma chyba'
        toast.error('Chyba pri načítavaní kategórií: ' + errorMessage)
      }
    }, selectCategory(categoryId: number | undefined) {
      if (categoryId) {
        this.post.category_id = categoryId
      }
    },

    getSelectedCategoryName(): string {
      const category = this.postCategories.find(cat => cat.id === this.post.category_id)
      return category ? category.name : ''
    },

    generateSlug(title: string): string {
      // Convert Slovak characters to ASCII equivalents
      const slug = title
        .replace(/[áä]/g, 'a')
        .replace(/[č]/g, 'c')
        .replace(/[ď]/g, 'd')
        .replace(/[é]/g, 'e')
        .replace(/[í]/g, 'i')
        .replace(/[ĺľ]/g, 'l')
        .replace(/[ň]/g, 'n')
        .replace(/[óô]/g, 'o')
        .replace(/[ŕ]/g, 'r')
        .replace(/[š]/g, 's')
        .replace(/[ť]/g, 't')
        .replace(/[ú]/g, 'u')
        .replace(/[ý]/g, 'y')
        .replace(/[ž]/g, 'z')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')

      return slug
    },

    async savePost() {
      // Validation
      if (!this.post.title?.trim()) {
        toast.error('Nadpis je povinný')
        return
      }

      if (!this.post.content?.trim()) {
        toast.error('Obsah článku je povinný')
        return
      }

      if (!this.post.category_id) {
        toast.error('Vyberte kategóriu článku')
        return
      }

      this.isSubmitting = true

      try {
        const data = {
          title: this.post.title.trim(),
          slug: this.post.slug?.trim() || undefined,
          content: this.post.content,
          image: this.post.image,
          category_id: this.post.category_id,
        }

        const response: ResponseData = await PostDataService.add(data)
        this.post.id = response.data.id

        // Upload image if one was selected
        if (this.imageData) {
          try {
            // Generate a unique filename for the image
            const timestamp = Date.now()
            const fileExtension = this.imageData.name.split('.').pop() || 'jpg'
            const fileName = `post_${this.post.id}_${timestamp}.${fileExtension}`

            const imageResponse: { data: { status: string; message: string; image_url: string } } = await PostDataService.uploadImage(this.post.id, this.imageData, fileName)

            if (imageResponse.data.status === 'success') {
              this.$emit('imageUrl', imageResponse.data.image_url)
              toast.success('Článok a obrázok boli úspešne pridané!')
            } else {
              toast.warn('Článok bol pridaný, ale nastala chyba pri nahrávaní obrázka: ' + imageResponse.data.message)
            }
          } catch (imageError: unknown) {
            console.error('Image upload error:', imageError)
            let errorMessage = 'Neznáma chyba'

            // Better error handling for different types of errors
            if (imageError instanceof Error) {
              errorMessage = imageError.message
            } else if (typeof imageError === 'object' && imageError !== null && 'response' in imageError) {
              const axiosError = imageError as { response?: { data?: { message?: string }; status?: number } }
              if (axiosError.response?.data?.message) {
                errorMessage = axiosError.response.data.message
              } else if (axiosError.response?.status === 422) {
                errorMessage = 'Nahraný súbor nespĺňa požiadavky (podporované formáty: JPG, PNG)'
              }
            }

            toast.warn('Článok bol pridaný, ale nastala chyba pri nahrávaní obrázka: ' + errorMessage)
          }
        } else {
          toast.success('Článok bol úspešne pridaný!')
        }

        this.submitted = true
      } catch (error: unknown) {
        console.error('Error saving post:', error)
        const errorMessage = error instanceof Error ? error.message : 'Neznáma chyba'
        toast.error('Chyba pri ukladaní článku: ' + errorMessage)
      } finally {
        this.isSubmitting = false
      }
    },

    newPost() {
      this.submitted = false
      this.isSubmitting = false
      this.post = {
        title: '',
        content: '',
        image: '',
        category_id: this.postCategories.length > 0 ? this.postCategories[0].id : undefined,
      } as Post
      this.imageData = null
      this.imagePreview = null
    },

    backToList() {
      this.$router.push({ name: 'Zoznam článkov' })
    },

    processImageFile(file: File) {
      // Validate file size (10MB max)
      const maxSize = 10 * 1024 * 1024 // 10MB in bytes
      if (file.size > maxSize) {
        toast.error('Súbor je príliš veľký. Maximálna veľkosť je 10MB.')
        return
      }

      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif']
      if (!allowedTypes.includes(file.type)) {
        toast.error('Nepodporovaný formát súboru. Povolené sú: JPG, PNG, GIF.')
        return
      }

      this.post.image = file.name
      this.imageData = file

      // Create preview
      const reader = new FileReader()
      reader.onload = (e) => {
        this.imagePreview = e.target?.result as string
      }
      reader.readAsDataURL(file)
    },

    updateImage(event: Event) {
      const target = event.target as HTMLInputElement
      const file = target.files?.[0]

      if (file) {
        this.processImageFile(file)
      } else {
        this.post.image = ''
        this.imageData = null
        this.imagePreview = null
      }
    },

    handleDrop(event: DragEvent) {
      event.preventDefault()
      const files = event.dataTransfer?.files
      if (files && files.length > 0) {
        const file = files[0]
        this.processImageFile(file)
      }
    },

    removeImage() {
      this.post.image = ''
      this.imageData = null
      this.imagePreview = null

      // Clear file input
      const fileInput = document.getElementById('image-upload') as HTMLInputElement
      if (fileInput) {
        fileInput.value = ''
      }
    },

    goBack() {
      // Check if there are unsaved changes
      if (this.post.title || this.post.content || this.imageData) {
        this.isLeaveWarningOpen = true
      } else {
        this.$router.go(-1)
      }
    },
    handleBeforeUnload(e: BeforeUnloadEvent) {
      if (!this.submitted && (this.post.title || this.post.content || this.imageData)) {
        e.preventDefault()
        e.returnValue = 'Máte neuložené zmeny. Naozaj chcete opustiť túto stránku?'
        return e.returnValue
      }
    },
    confirmLeave() {
      this.isLeaveWarningOpen = false
      this.confirmedLeave = true
      // Use nextTick to ensure the data change is processed before navigation
      this.$nextTick(() => {
        if (this.pendingRoute) {
          this.$router.push(this.pendingRoute)
        } else {
          this.$router.push({ name: 'Zoznam článkov' })
        }
      })
    },
    cancelLeave() {
      this.isLeaveWarningOpen = false
      this.pendingRoute = null
    },
  },

  // Prevent accidental navigation away from unsaved changes
  beforeRouteLeave(to, from, next) {
    // If user already confirmed to leave or has successfully submitted, allow navigation
    if (this.confirmedLeave || this.submitted) {
      next()
      return
    }

    // Check if there are unsaved changes
    if (this.post.title || this.post.content || this.imageData) {
      this.pendingRoute = to // Store where user wants to go
      this.isLeaveWarningOpen = true
      next(false) // Prevent navigation initially
    } else {
      next()
    }
  },
})
</script>
